import {ReactNode, Ref, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {Box, Button, IconButton, Spinner, Text} from '@chakra-ui/react';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import {DriveFile} from '../../drive/types';
import {displayedDimensions} from '../../domain/aspectRatio';
import {fitSize, Size, zoomPercent} from '../../domain/zoomPan';
import {SwipeAction} from '../../domain/swipe';
import {useLongPress} from '../../hooks/useLongPress';
import {useZoomPan} from './useZoomPan';
import {useStageGestures} from './useStageGestures';
import {ViewerImage} from './useViewerImage';
import {AnchorLayer, StageAnchors} from './AnchorLayer';

const TRANSITION = 'transform 0.2s ease-out, opacity 0.2s ease-out';

/**
 * How long the previous photo may stay on screen while the next one decodes. Long enough to bridge
 * an original's decoding, short enough that a slow download shows the spinner rather than a photo
 * the actions no longer apply to.
 */
const HOLD_PREVIOUS_MS = 500;

/** How many decoded URLs are remembered (blob URLs of evicted originals are revoked anyway). */
const MAX_READY_URLS = 40;

/** What the stage last showed: kept on screen until the next picture can replace it whole. */
interface ShownPicture {
    fileId: string;
    url: string;
    size: Size | null;
}

const imageStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    WebkitTouchCallout: 'none',
} as const;

/** The decoded image the stage shows for a photo, e.g. for the histogram to read its pixels. */
export interface ShownImage {
    fileId: string;
    url: string;
    element: HTMLImageElement;
}

/** What the viewer's keyboard can do with the stage. */
export interface ViewerStageHandle {
    zoomBy: (factor: number) => void;
    resetZoom: () => void;
}

interface ViewerStageProps {
    ref?: Ref<ViewerStageHandle>;
    file: DriveFile;
    image: ViewerImage;
    onPrevious?: () => void;
    onNext?: () => void;
    /** The swipes that do something (at fit, with a finger). */
    swipes: Partial<Record<SwipeAction, () => void>>;
    /** Zooming in fetches the original; the stage reports when it is zoomed in or back at fit. */
    onZoomedChange: (zoomed: boolean) => void;
    onContextMenu: (e: React.MouseEvent) => void;
    /** Told what the stage shows of the current photo once it is decoded (null while there is none). */
    onShownImage?: (shown: ShownImage | null) => void;
    /** Comments' regions on the picture, and drawing a new one. */
    anchors?: StageAnchors;
    /** Overlays on the stage (e.g. the file name on phones). */
    children?: ReactNode;
}

/**
 * The viewer's picture with its zoom, pan and swipe gestures, loading state and prev/next arrows.
 * Each photo starts at fit. Pictures never flash black when they change: every image stays hidden
 * until decoded, and the one shown before stays until then (another photo's only briefly), each
 * <img> keyed by its URL so React keeps the elements that are already decoded.
 */
export function ViewerStage({
    ref,
    file,
    image,
    onPrevious,
    onNext,
    swipes,
    onZoomedChange,
    onContextMenu,
    onShownImage,
    anchors,
    children,
}: ViewerStageProps) {
    // The URLs whose images are decoded and can be shown whole.
    const [ready, setReady] = useState<string[]>([]);
    const [natural, setNatural] = useState<{ key: string; size: Size }>();
    const [lastShown, setLastShown] = useState<ShownPicture>();
    const [holdExpiredFor, setHoldExpiredFor] = useState<string>();
    // The URLs being decoded: the ref below runs on every render, and must not start it again.
    const decoding = useRef(new Set<string>());
    // The mounted <img> elements by URL, to hand the shown one to `onShownImage`.
    const elements = useRef(new Map<string, HTMLImageElement>());
    // What is pictured: the photo, or an older version of it, which may differ in size.
    const pictureKey = image.revisionId ? `${file.id}@${image.revisionId}` : file.id;
    // Drive's dimensions are the current original's; an older version, or a picture without them, is
    // measured once shown.
    const imageSize = (image.revisionId ? null : displayedDimensions(file))
        ?? (natural?.key === pictureKey ? natural.size : null);
    const zoom = useZoomPan(imageSize, pictureKey);
    const gestures = useStageGestures({zoom, image: imageSize, swipes});
    const longPress = useLongPress(onContextMenu);

    useImperativeHandle(ref, () => ({zoomBy: (factor) => zoom.zoomBy(factor), resetZoom: zoom.reset}));

    useEffect(() => {
        onZoomedChange(zoom.zoomed);
    }, [zoom.zoomed, onZoomedChange]);

    function handleLoaded(node: HTMLImageElement, url: string, key: string) {
        if (decoding.current.has(url)) return;
        if (key === pictureKey) {
            const size = {width: node.naturalWidth, height: node.naturalHeight};
            setNatural((prev) => prev?.key === key ? prev : {key, size});
        }
        decoding.current.add(url);
        const markReady = () => {
            decoding.current.delete(url);
            setReady((prev) => prev.includes(url) ? prev : [...prev.slice(-MAX_READY_URLS + 1), url]);
        };
        // `load` comes before decoding: a large image shown at once would paint blank for a moment.
        if (typeof node.decode === 'function') node.decode().then(markReady, markReady);
        else markReady();
    }

    // Guards against a race with the browser cache (e.g. previews warmed by the prefetch): an <img>
    // already complete when mounted may have fired `load` before React attached its handler.
    function loadedRef(url: string, key: string) {
        return (node: HTMLImageElement | null) => {
            if (node) elements.current.set(url, node);
            else elements.current.delete(url);
            if (node?.complete && node.naturalWidth > 0 && !ready.includes(url)) handleLoaded(node, url, key);
        };
    }

    const {displayUrl, placeholderUrl, loadError, source} = image;
    // This photo's images, bottom up: Drive's preview under a loading original, then what is shown.
    const targets: { url: string; main: boolean }[] = [];
    if (placeholderUrl) targets.push({url: placeholderUrl, main: false});
    if (displayUrl && !loadError) targets.push({url: displayUrl, main: true});
    let topReady = -1;
    targets.forEach((t, i) => {
        if (ready.includes(t.url)) topReady = i;
    });
    // Once an image is shown, the ones under it can go.
    const layers = topReady > 0 ? targets.slice(topReady) : targets;
    const shownUrl = topReady >= 0 ? targets[topReady].url : undefined;

    useEffect(() => {
        if (shownUrl) setLastShown({fileId: file.id, url: shownUrl, size: imageSize});
        // Only on a change of what is shown; the size goes with it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shownUrl, file.id]);

    useEffect(() => {
        const element = shownUrl ? elements.current.get(shownUrl) : undefined;
        onShownImage?.(shownUrl && element ? {fileId: file.id, url: shownUrl, element} : null);
    }, [shownUrl, file.id, onShownImage]);

    const failed = !!loadError || source === 'unavailable';
    const waiting = topReady < 0 && !failed;
    // E.g. the original over a preview that "Eredeti" was just turned off for: no flash in between.
    const holdOwn = waiting && lastShown?.fileId === file.id && !layers.some((l) => l.url === lastShown.url);
    // The previous photo, while this one decodes.
    const holdPrevious = waiting && !!lastShown && lastShown.fileId !== file.id && holdExpiredFor !== file.id;
    // An older version being downloaded spins over the picture held until then, which is not it.
    const isLoading = (waiting && !holdPrevious) || (image.revisionLoading && !failed);

    useEffect(() => {
        if (!holdPrevious) return;
        const timer = window.setTimeout(() => setHoldExpiredFor(file.id), HOLD_PREVIOUS_MS);
        return () => window.clearTimeout(timer);
    }, [holdPrevious, file.id]);

    const {view, bounds} = zoom;
    const drag = gestures.drag;
    const dx = drag?.axis === 'x' ? drag.offset : 0;
    const dy = drag?.axis === 'y' ? drag.offset : 0;
    const animate = !gestures.active && !zoom.wheeling && !zoom.resizing;

    /** `key` is what the image pictures: `pictureKey` for the current picture, else a stand-in's photo. */
    function renderImage(url: string, key: string, main: boolean, visible: boolean) {
        return <img
            key={url}
            ref={loadedRef(url, key)}
            src={url}
            // Only the photo's own shown image is its picture; the others are momentary stand-ins.
            alt={main ? file.name : ''}
            aria-hidden={main ? undefined : true}
            draggable={false}
            style={{...imageStyle, opacity: visible ? 1 : 0}}
            onLoad={(e) => handleLoaded(e.currentTarget, url, key)}
            onError={main && source === 'preview' ? image.onPreviewError : undefined}
            referrerPolicy="no-referrer"
        />;
    }

    // Keyed by photo, so a held photo keeps its decoded <img> from when it was the current one.
    const heldBox = holdPrevious && lastShown ? fitSize(lastShown.size, bounds.stage) : undefined;
    const frames = [
        holdPrevious && lastShown && heldBox &&
            <Box
                key={lastShown.fileId}
                position="absolute"
                left="50%"
                top="50%"
                pointerEvents="none"
                style={{width: heldBox.width, height: heldBox.height, transform: 'translate(-50%, -50%)'}}
            >
                {renderImage(lastShown.url, lastShown.fileId, false, true)}
            </Box>,
        <Box
            key={file.id}
            position="absolute"
            left="50%"
            top="50%"
            style={{
                width: bounds.fit.width,
                height: bounds.fit.height,
                transform: `translate(-50%, -50%) translate(${view.x + dx}px, ${view.y + dy}px) scale(${view.scale})`,
                opacity: dy ? 1 - Math.min(Math.abs(dy) / Math.max(bounds.stage.height, 1), 0.6) : 1,
                transition: animate ? TRANSITION : 'none',
            }}
            {...longPress}
        >
            {holdOwn && lastShown && renderImage(lastShown.url, file.id, false, true)}
            {layers.map((l) => renderImage(l.url, pictureKey, l.main, ready.includes(l.url)))}
            {anchors && <AnchorLayer anchors={anchors} scale={view.scale}/>}
        </Box>,
    ];

    return <Box
        ref={zoom.stageRef}
        position="relative"
        flex={1}
        minW={0}
        minH={0}
        overflow="hidden"
        touchAction="none"
        userSelect="none"
        cursor={zoom.zoomed ? (gestures.active ? 'grabbing' : 'grab') : undefined}
        data-viewer-stage
        {...gestures.handlers}
    >
        {frames}

        {isLoading &&
            <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" pointerEvents="none">
                <Spinner color="white" boxSize="12"/>
            </Box>
        }
        {(loadError || source === 'unavailable') &&
            <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" pointerEvents="none">
                <Text color="red.300">{loadError ? 'A kép betöltése sikertelen.' : 'A kép nem jeleníthető meg.'}</Text>
            </Box>
        }

        <IconButton
            aria-label="Előző"
            position="absolute"
            left={4}
            top="50%"
            transform="translateY(-50%)"
            zIndex={2}
            disabled={!onPrevious}
            onClick={onPrevious}
            variant="ghost"
            color="white"
            _hover={{bg: 'whiteAlpha.200'}}
        >
            <ChevronLeft size={28}/>
        </IconButton>
        <IconButton
            aria-label="Következő"
            position="absolute"
            right={4}
            top="50%"
            transform="translateY(-50%)"
            zIndex={2}
            disabled={!onNext}
            onClick={onNext}
            variant="ghost"
            color="white"
            _hover={{bg: 'whiteAlpha.200'}}
        >
            <ChevronRight size={28}/>
        </IconButton>

        {zoom.zoomed &&
            <Button
                position="absolute"
                top={3}
                right={3}
                zIndex={2}
                size="xs"
                variant="solid"
                bg="blackAlpha.700"
                color="white"
                _hover={{bg: 'blackAlpha.800'}}
                fontVariantNumeric="tabular-nums"
                title={image.zoomOriginalLoading ? 'Eredeti minőség betöltése…' : 'Vissza a teljes képhez (0)'}
                onClick={zoom.reset}
            >
                {image.zoomOriginalLoading && <Spinner size="xs"/>}
                {zoomPercent(view.scale, imageSize, bounds.fit)}%
            </Button>
        }

        {children}
    </Box>;
}
